import React, { useEffect, useMemo, useState } from 'react';
import RSelect from 'react-select';
import { getData, putData, Postdata } from '../../API/GlobalApi';
import { formatApiError } from '../../Utils/apiErrors';
import { reactSelectStyles } from '../../Utils/selectBoxStyle';

// ---------- design tokens ----------
const C = {
  ink:        '#1E293B',
  paper:      '#FFFFFF',
  paperHi:    '#F8F6FF',
  rule:       '#E2E8F0',
  ruleSoft:   '#F1F5F9',
  muted:      '#64748B',
  mutedSoft:  '#94A3B8',
  accent:     '#7F56DA',  // portal purple
  accentDeep: '#5B3EA8',
  accentSoft: '#F3EEFF',
  warn:       '#B45309',
  warnSoft:   '#FFFBEB',
  alarm:      '#9F1239',
  alarmSoft:  '#FFF1F2',
};

// ---------- helpers ----------

// Mirrors backend students/services/lag_change.py::_academic_group_position so
// we filter out demotion targets before the user can pick them. Names follow
// the canonical signal-generated pattern: <mode> <N> for cycle, "Pass Out" terminal.
const PASS_OUT_RE = /\bpass[\s\-_]*out\b/i;
const MODE_PREFIX = {
  Annual: 'Year',
  Semester: 'Semester',
  Trimester: 'Trimester',
  Quarter: 'Quarter',
};
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const agPosition = (ag, courseMode) => {
  if (!ag?.name) return null;
  const name = ag.name.trim();
  if (PASS_OUT_RE.test(name)) return Infinity;
  const prefix = MODE_PREFIX[courseMode];
  if (prefix) {
    const re = new RegExp('^' + escapeRe(prefix) + '\\s+(\\d+)$', 'i');
    const m = name.match(re);
    if (m) return parseInt(m[1], 10);
  }
  return null;
};

// "General" when the specialization has no real name. The nested serializer
// substitutes the course alias/name in those cases, so detect either form.
const specDisplayName = (spec) => {
  if (!spec) return '';
  const raw = (spec.name || '').trim();
  const courseAlias = (spec.course?.alias || '').trim();
  const courseName = (spec.course?.name || '').trim();
  if (!raw || raw === '-' || raw === courseAlias || raw === courseName) return 'General';
  return raw;
};

// API responses are sometimes plain arrays, sometimes paginated objects.
const arrify = (res) => (Array.isArray(res) ? res : (Array.isArray(res?.results) ? res.results : []));

// ---------- atoms ----------

const Label = ({ children, idx }) => (
  <span style={{
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: C.muted,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 6,
  }}>
    {idx ? <span style={{ color: C.mutedSoft }}>{String(idx).padStart(2, '0')}</span> : null}
    {children}
  </span>
);


// Wrapper: takes a primitive id + items array, exposes an onChange(id) callback.
// Memoizes options + selected lookup so react-select stays performant.
const Dropdown = ({ value, onChange, items, getLabel, placeholder, disabled }) => {
  const options = useMemo(
    () => (items || []).map((item) => ({
      value: String(item.id),
      label: getLabel(item),
    })),
    [items, getLabel]
  );
  const selected = useMemo(
    () => options.find((o) => o.value === String(value)) || null,
    [options, value]
  );
  return (
    <RSelect
      value={selected}
      onChange={(opt) => onChange(opt ? opt.value : '')}
      options={options}
      placeholder={placeholder}
      isDisabled={disabled || !options.length}
      isSearchable
      menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
      styles={reactSelectStyles}
      noOptionsMessage={() => 'No options'}
    />
  );
};

const Capacity = ({ used, total }) => {
  const ratio = total > 0 ? Math.min(used / total, 1) : 0;
  const tone = !total ? C.alarm : ratio >= 1 ? C.alarm : ratio >= 0.85 ? C.warn : C.accent;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        fontSize: 12, color: C.ink,
      }}>
        <span style={{ letterSpacing: '0.06em' }}>SEATS</span>
        <span><strong>{used}</strong>{' / '}{total || 0}</span>
      </div>
      <div style={{ height: 4, background: C.ruleSoft, position: 'relative' }}>
        <div style={{
          position: 'absolute', inset: '0 auto 0 0',
          width: `${Math.max(ratio * 100, total ? 2 : 0)}%`,
          background: tone,
          transition: 'width 240ms ease',
        }} />
      </div>
      {!total ? (
        <span style={{ fontSize: 11, color: C.alarm, letterSpacing: '0.04em' }}>
          No intake configured — set a limit below to enable promotion.
        </span>
      ) : null}
    </div>
  );
};

// ---------- main component ----------

export default function BulkPromoteModal({ open, onClose, onSuccess }) {

  // bootstrap
  const [defaultAyId, setDefaultAyId] = useState(null);

  // SOURCE
  const [courses, setCourses] = useState([]);
  const [srcCourseId, setSrcCourseId] = useState('');
  const [specs, setSpecs] = useState([]);
  const [srcSpecId, setSrcSpecId] = useState('');
  const [ags, setAgs] = useState([]);
  const [srcAgId, setSrcAgId] = useState('');
  const [lags, setLags] = useState([]);
  const [srcLagId, setSrcLagId] = useState('');

  // STUDENTS
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  // TARGET
  const [tgtAgId, setTgtAgId] = useState('');
  const [tgtLagId, setTgtLagId] = useState('');
  const [tgtLags, setTgtLags] = useState([]);

  // intake editor
  const [intakeDraft, setIntakeDraft] = useState('');
  const [intakeSaving, setIntakeSaving] = useState(false);

  // submit / results
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  // toast
  const [toast, setToast] = useState(null);
  const flash = (msg, kind = 'info') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 3500);
  };

  // -- bootstrap --
  useEffect(() => {
    if (!open) return;
    (async () => {
      const ys = arrify(await getData('academics/academic-years/'));
      if (ys.length) setDefaultAyId(ys[0].id);
    })();
  }, [open]);

  useEffect(() => {
    if (open) return;
    setSrcCourseId(''); setSrcSpecId(''); setSrcAgId(''); setSrcLagId('');
    setSpecs([]); setAgs([]); setLags([]);
    setStudents([]); setSelected(new Set());
    setTgtAgId(''); setTgtLagId(''); setTgtLags([]);
    setIntakeDraft(''); setResults(null);
  }, [open]);

  useEffect(() => {
    if (!open || !defaultAyId) return;
    (async () => {
      setCourses(arrify(await getData(`academics/academic-years/${defaultAyId}/courses/`)));
    })();
  }, [open, defaultAyId]);

  // cascades — source
  useEffect(() => {
    setSrcSpecId(''); setSpecs([]);
    setSrcAgId(''); setAgs([]);
    setSrcLagId(''); setLags([]);
    setStudents([]); setSelected(new Set());
    if (!srcCourseId || !defaultAyId) return;
    (async () => {
      setSpecs(arrify(await getData(
        `academics/academic-years/${defaultAyId}/courses/${srcCourseId}/specializations/`
      )));
    })();
  }, [srcCourseId, defaultAyId]);

  useEffect(() => {
    setSrcAgId(''); setAgs([]);
    setSrcLagId(''); setLags([]);
    setStudents([]); setSelected(new Set());
    if (!srcSpecId) return;
    (async () => {
      setAgs(arrify(await getData(
        `academics/academic-years/${defaultAyId}/courses/${srcCourseId}/specializations/${srcSpecId}/academic-groups/`
      )));
    })();
  }, [srcSpecId, srcCourseId, defaultAyId]);

  useEffect(() => {
    setSrcLagId(''); setLags([]);
    setStudents([]); setSelected(new Set());
    if (!srcAgId) return;
    (async () => {
      setLags(arrify(await getData(
        `academics/academic-years/${defaultAyId}/courses/${srcCourseId}/specializations/${srcSpecId}/academic-groups/${srcAgId}/lags/`
      )));
    })();
  }, [srcAgId, srcSpecId, srcCourseId, defaultAyId]);

  useEffect(() => {
    setStudents([]); setSelected(new Set());
    if (!srcLagId) return;
    setStudentsLoading(true);
    (async () => {
      try {
        setStudents(arrify(await getData(
          `academics/academic-years/${defaultAyId}/courses/${srcCourseId}/specializations/${srcSpecId}/academic-groups/${srcAgId}/lags/${srcLagId}/students/`
        )));
      } finally {
        setStudentsLoading(false);
      }
    })();
  }, [srcLagId, srcAgId, srcSpecId, srcCourseId, defaultAyId]);

  // target — reset when source AG changes
  useEffect(() => {
    setTgtAgId(''); setTgtLagId(''); setTgtLags([]);
  }, [srcAgId]);

  useEffect(() => {
    setTgtLagId(''); setTgtLags([]); setIntakeDraft('');
    if (!tgtAgId) return;
    (async () => {
      setTgtLags(arrify(await getData(
        `academics/academic-years/${defaultAyId}/courses/${srcCourseId}/specializations/${srcSpecId}/academic-groups/${tgtAgId}/lags/`
      )));
    })();
  }, [tgtAgId, srcCourseId, srcSpecId, defaultAyId]);

  // derivations
  const srcCourse = courses.find(c => String(c.id) === String(srcCourseId));
  const srcSpec = specs.find(s => String(s.id) === String(srcSpecId));
  const srcAg = ags.find(a => String(a.id) === String(srcAgId));
  const srcLag = lags.find(l => String(l.id) === String(srcLagId));
  const courseMode = srcCourse?.mode;
  const srcAgPos = srcAg ? agPosition(srcAg, courseMode) : null;

  // target AG options — only AGs strictly after source (no demotion).
  const targetAgOptions = useMemo(() => {
    if (!ags.length) return [];
    if (srcAgPos === null) return ags.filter(ag => agPosition(ag, courseMode) !== null);
    return ags.filter(ag => {
      const p = agPosition(ag, courseMode);
      return p !== null && p > srcAgPos;
    });
  }, [ags, srcAgPos, courseMode]);

  const tgtLag = tgtLags.find(l => String(l.id) === String(tgtLagId));
  const tgtIntake = tgtLag?.intake_limit || 0;
  const tgtUsed = (tgtLag && typeof tgtLag.intake_limit !== 'undefined')
    // The nested-readonly LAG serializer doesn't carry registered_students; we
    // can only show capacity in the academic-year-scoped sense if the value
    // ships. Show 0 as a neutral default; the real cap check happens server-side.
    ? 0
    : 0;

  const allLoaded = !!(srcCourseId && srcSpecId && srcAgId && srcLagId);
  const canSubmit = allLoaded && tgtAgId && tgtLagId && selected.size > 0 && !submitting && tgtIntake > 0;

  const toggleStudent = (uid) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  };
  const selectAll = (checked) => {
    setSelected(checked ? new Set(students.map(s => s.user_id)) : new Set());
  };

  const saveIntakeLimit = async () => {
    if (!tgtLag) return;
    const n = parseInt(intakeDraft, 10);
    if (Number.isNaN(n) || n <= 0 || n > 1000) {
      flash('Intake limit must be a positive integer between 1 and 1000.', 'danger');
      return;
    }
    setIntakeSaving(true);
    try {
      const res = await putData(`academics/least-academic-group/${tgtLag.id}/`, {
        academic_group_id: tgtAgId,
        name: tgtLag.name,
        intake_limit: n,
      });
      const isFieldError =
        !res ||
        res.error !== undefined ||
        res.detail !== undefined ||
        Array.isArray(res.intake_limit) ||
        Array.isArray(res.name) ||
        typeof res.id !== 'number';
      if (isFieldError) {
        flash(formatApiError(res, 'Failed to update intake limit'), 'danger');
        return;
      }
      flash(`Intake limit for ${tgtLag.name} set to ${n}.`, 'success');
      setIntakeDraft('');
      setTgtLags(arrify(await getData(
        `academics/academic-years/${defaultAyId}/courses/${srcCourseId}/specializations/${srcSpecId}/academic-groups/${tgtAgId}/lags/`
      )));
    } finally {
      setIntakeSaving(false);
    }
  };

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const res = await Postdata('students/bulk-promote-students/', {
        student_ids: Array.from(selected),
        to_lag: parseInt(tgtLagId, 10),
        notes: 'Bulk promotion via dashboard',
      });
      if (res?.summary && Array.isArray(res?.results)) {
        setResults(res);
        if (res.summary.failed === 0) {
          flash(`Promoted ${res.summary.succeeded} student(s).`, 'success');
        } else {
          flash(`Done — ${res.summary.succeeded} succeeded, ${res.summary.failed} failed.`, 'warn');
        }
        if (typeof onSuccess === 'function') onSuccess();

        // Refresh state so the modal reflects the post-promote world: the
        // source roster now has fewer rows, the target LAG's seat count went up,
        // and the user shouldn't be able to immediately re-submit the same set.
        const succeededIds = new Set(
          res.results.filter(r => r.success).map(r => r.student_id)
        );
        if (succeededIds.size > 0) {
          // Drop promoted students from the visible source roster optimistically;
          // also fetch fresh from the API so anything we missed gets corrected.
          setStudents(prev => prev.filter(s => !succeededIds.has(s.user_id)));
          setSelected(prev => {
            const next = new Set(prev);
            succeededIds.forEach(id => next.delete(id));
            return next;
          });
          if (srcLagId) {
            const fresh = arrify(await getData(
              `academics/academic-years/${defaultAyId}/courses/${srcCourseId}/specializations/${srcSpecId}/academic-groups/${srcAgId}/lags/${srcLagId}/students/`
            ));
            setStudents(fresh);
          }
          if (tgtAgId) {
            const freshLags = arrify(await getData(
              `academics/academic-years/${defaultAyId}/courses/${srcCourseId}/specializations/${srcSpecId}/academic-groups/${tgtAgId}/lags/`
            ));
            setTgtLags(freshLags);
          }
        }
      } else {
        flash(formatApiError(res, 'Bulk promote failed'), 'danger');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // ---------- render ----------
  const allSelected = students.length > 0 && selected.size === students.length;
  const someSelected = selected.size > 0 && !allSelected;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 20, 25, 0.55)',
        backdropFilter: 'blur(2px)',
        zIndex: 9000,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '40px 24px',
        animation: 'bp-fade 200ms ease',
      }}
    >
      <style>{`
        @keyframes bp-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bp-rise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .bp-modal { animation: bp-rise 280ms cubic-bezier(.2,.8,.2,1); }
        .bp-row:hover { background: ${C.paperHi}; }
        .bp-row.bp-row-selected { background: ${C.accentSoft}; }
        .bp-row.bp-row-selected td:first-child {
          box-shadow: inset 3px 0 0 ${C.accent};
        }
        .bp-checkbox { accent-color: ${C.accent}; transform: scale(1.1); }
        .bp-icon-btn:hover { background: ${C.paperHi}; }
        .bp-action:disabled { opacity: 0.45; cursor: not-allowed; }
        .bp-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
        .bp-scroll::-webkit-scrollbar-track { background: ${C.ruleSoft}; }
        .bp-scroll::-webkit-scrollbar-thumb { background: #C4B5FD; border-radius: 3px; }
      `}</style>

      <div
        className="bp-modal"
        style={{
          width: '100%',
          maxWidth: 1180,
          maxHeight: 'calc(100vh - 80px)',
          background: C.paper,
          color: C.ink,
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          display: 'flex', flexDirection: 'column',
          borderRadius: '8px',
          boxShadow: '0 20px 60px -10px rgba(0,0,0,0.20), 0 0 0 1px rgba(127,86,218,0.15)',
          overflow: 'hidden',
        }}
      >

        {/* HEADER */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 28px',
          borderBottom: `1px solid ${C.rule}`,
          background: C.accent, color: '#FFFFFF',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 11, letterSpacing: '0.2em', color: 'rgba(255,255,255,0.65)',
            }}>
              ⌘ COHORT TRANSIT
            </span>
            <span style={{ width: 1, height: 16, background: 'rgba(255,255,255,0.35)' }} />
            <span style={{
              fontSize: 22, fontWeight: 600, letterSpacing: '-0.01em',
            }}>
              Bulk Promotion
            </span>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{
              background: 'transparent', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.4)',
              width: 32, height: 32, cursor: 'pointer', fontSize: 16, borderRadius: '4px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            ✕
          </button>
        </div>

        {/* BODY (scrollable) */}
        <div className="bp-scroll" style={{ overflowY: 'auto', flex: 1 }}>

          {/* SOURCE — sticky */}
          <section style={{
            position: 'sticky', top: 0, zIndex: 5,
            background: '#FFFFFF',
            borderBottom: `1px solid ${C.rule}`,
            padding: '20px 28px 18px',
          }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', color: C.muted,
                }}>FROM</span>
                <span style={{ fontSize: 13, color: C.muted }}>
                  Pick the cohort whose students you want to promote.
                </span>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              <div>
                <Label idx={1}>Course</Label>
                <Dropdown
                  value={srcCourseId}
                  onChange={setSrcCourseId}
                  items={courses}
                  getLabel={(c) => (c.alias ? `${c.name} (${c.alias})` : c.name)}
                  placeholder="Select course"
                />
              </div>
              <div>
                <Label idx={2}>Specialization</Label>
                <Dropdown
                  value={srcSpecId}
                  onChange={setSrcSpecId}
                  items={specs}
                  getLabel={specDisplayName}
                  disabled={!srcCourseId}
                  placeholder={srcCourseId ? 'Select specialization' : '—'}
                />
              </div>
              <div>
                <Label idx={3}>Academic Group</Label>
                <Dropdown
                  value={srcAgId}
                  onChange={setSrcAgId}
                  items={ags}
                  getLabel={(a) => a.name}
                  disabled={!srcSpecId}
                  placeholder={srcSpecId ? 'Select group' : '—'}
                />
              </div>
              <div>
                <Label idx={4}>Class</Label>
                <Dropdown
                  value={srcLagId}
                  onChange={setSrcLagId}
                  items={lags}
                  getLabel={(l) => l.name}
                  disabled={!srcAgId}
                  placeholder={srcAgId ? 'Select class' : '—'}
                />
              </div>
            </div>
          </section>

          {/* STUDENTS TABLE — fixed-size, header sticky, body scroll */}
          <section style={{ padding: '20px 28px' }}>
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              marginBottom: 10,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                <span style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', color: C.muted,
                }}>STUDENTS</span>
                {allLoaded ? (
                  <span style={{ fontSize: 13, color: C.muted }}>
                    <strong style={{ color: C.ink }}>{selected.size}</strong>
                    {' selected · '}
                    <strong style={{ color: C.ink }}>{students.length}</strong>
                    {' in this class'}
                  </span>
                ) : (
                  <span style={{ fontSize: 13, color: C.mutedSoft }}>
                    Pick all four above to load the roster.
                  </span>
                )}
              </div>
              {allLoaded && students.length ? (
                <button
                  type="button"
                  onClick={() => selectAll(!allSelected)}
                  style={{
                    background: 'transparent', border: `1px solid ${C.accent}`, color: C.accent,
                    padding: '4px 10px', fontSize: 11, borderRadius: '4px',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
                  }}
                >
                  {allSelected ? '— Deselect All' : '+ Select All'}
                </button>
              ) : null}
            </div>

            <div style={{
              border: `1px solid ${C.rule}`,
              background: '#FFFFFF',
              height: 320,
              display: 'flex', flexDirection: 'column',
            }}>
              {/* header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '44px 180px 1fr 140px',
                background: C.paperHi,
                borderBottom: `1px solid ${C.rule}`,
                fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 700,
                color: C.muted,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              }}>
                <div style={{ padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <input
                    type="checkbox"
                    className="bp-checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={(e) => selectAll(e.target.checked)}
                    disabled={!students.length}
                  />
                </div>
                <div style={{ padding: '10px 8px' }}>PRN</div>
                <div style={{ padding: '10px 8px' }}>Name</div>
                <div style={{ padding: '10px 8px' }}>Mobile</div>
              </div>

              {/* body */}
              <div className="bp-scroll" style={{ overflowY: 'auto', flex: 1 }}>
                {!allLoaded ? (
                  <div style={{
                    height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: C.mutedSoft, fontSize: 13,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    letterSpacing: '0.06em',
                  }}>
                    awaiting source selection
                  </div>
                ) : studentsLoading ? (
                  <div style={{
                    height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: C.muted, fontSize: 13,
                  }}>
                    Loading…
                  </div>
                ) : students.length === 0 ? (
                  <div style={{
                    height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: C.muted, fontSize: 13,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    letterSpacing: '0.06em',
                  }}>
                    no students in this class
                  </div>
                ) : (
                  <div>
                    {students.map((s) => {
                      const isSel = selected.has(s.user_id);
                      return (
                        <div
                          key={s.user_id}
                          className={'bp-row' + (isSel ? ' bp-row-selected' : '')}
                          onClick={() => toggleStudent(s.user_id)}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '44px 180px 1fr 140px',
                            cursor: 'pointer',
                            borderBottom: `1px solid ${C.ruleSoft}`,
                            alignItems: 'center',
                          }}
                        >
                          <div style={{ padding: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <input
                              type="checkbox"
                              className="bp-checkbox"
                              checked={isSel}
                              onChange={() => toggleStudent(s.user_id)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div style={{
                            padding: '10px 8px',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            fontSize: 12, color: C.ink,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}>
                            {s.permanent_registration_number || '—'}
                          </div>
                          <div style={{ padding: '10px 8px', fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.name || '—'}
                          </div>
                          <div style={{
                            padding: '10px 8px',
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                            fontSize: 12, color: C.muted,
                          }}>
                            {s.mobile || '—'}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* TARGET */}
          <section style={{
            padding: '20px 28px 24px',
            borderTop: `1px solid ${C.rule}`,
            background: C.paperHi,
          }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
              <span style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                fontSize: 11, fontWeight: 700, letterSpacing: '0.22em', color: C.accent,
              }}>TO</span>
              <span style={{ fontSize: 13, color: C.muted }}>
                Target placement. Course and Specialization are locked to the source. Earlier
                academic groups are hidden — the system will not demote students.
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
              <div>
                <Label idx={1}>Course</Label>
                <Dropdown
                  value={srcCourseId}
                  onChange={() => {}}
                  items={srcCourse ? [srcCourse] : []}
                  getLabel={(c) => (c.alias ? `${c.name} (${c.alias})` : c.name)}
                  disabled
                  placeholder="—"
                />
              </div>
              <div>
                <Label idx={2}>Specialization</Label>
                <Dropdown
                  value={srcSpecId}
                  onChange={() => {}}
                  items={srcSpec ? [srcSpec] : []}
                  getLabel={specDisplayName}
                  disabled
                  placeholder="—"
                />
              </div>
              <div>
                <Label idx={3}>Academic Group</Label>
                <Dropdown
                  value={tgtAgId}
                  onChange={setTgtAgId}
                  items={targetAgOptions}
                  getLabel={(a) => a.name}
                  disabled={!allLoaded || targetAgOptions.length === 0}
                  placeholder={
                    !allLoaded ? '—' :
                    targetAgOptions.length === 0 ? 'No forward groups available' :
                    'Select group'
                  }
                />
              </div>
              <div>
                <Label idx={4}>Class</Label>
                <Dropdown
                  value={tgtLagId}
                  onChange={setTgtLagId}
                  items={tgtLags}
                  getLabel={(l) =>
                    `${l.name}${l.intake_limit ? ` (${l.intake_limit})` : ' (no intake)'}`
                  }
                  disabled={!tgtAgId}
                  placeholder={tgtAgId ? 'Select class' : '—'}
                />
              </div>
            </div>

            {/* Capacity + intake editor */}
            {tgtLag ? (
              <div style={{
                marginTop: 16,
                padding: '14px 16px',
                background: '#FFFFFF',
                border: `1px solid ${C.rule}`,
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24,
              }}>
                <div>
                  <Capacity used={tgtUsed} total={tgtIntake} />
                  <div style={{
                    marginTop: 8, fontSize: 12, color: C.muted, lineHeight: 1.5,
                  }}>
                    Promoting <strong style={{ color: C.ink }}>{selected.size}</strong> student(s) into{' '}
                    <strong style={{ color: C.ink }}>{tgtLag.name}</strong>.
                    {tgtIntake > 0 && selected.size > tgtIntake ? (
                      <span style={{ color: C.alarm, fontWeight: 600 }}>
                        {' '}— exceeds intake limit; back-end will reject the overflow.
                      </span>
                    ) : null}
                  </div>
                </div>
                <div>
                  <Label>Update Intake Limit</Label>
                  <div style={{
                    display: 'flex', gap: 8, marginTop: 8, alignItems: 'stretch',
                  }}>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={intakeDraft}
                      onChange={(e) => setIntakeDraft(e.target.value)}
                      placeholder={String(tgtIntake || 0)}
                      style={{
                        flex: '1 1 auto',
                        padding: '6px 10px',
                        background: '#FFFFFF',
                        color: C.ink,
                        border: `1px solid ${C.rule}`,
                        borderRadius: 0,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        fontSize: 14,
                      }}
                    />
                    <button
                      type="button"
                      onClick={saveIntakeLimit}
                      disabled={intakeSaving || !intakeDraft}
                      className="bp-action"
                      style={{
                        padding: '6px 18px',
                        background: C.accent, color: '#FFFFFF',
                        border: 'none', borderRadius: '4px',
                        fontSize: 12, fontWeight: 600,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                        cursor: 'pointer',
                      }}
                    >
                      {intakeSaving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: C.mutedSoft }}>
                    1–1000. Empty / zero = class disabled for promotion.
                  </div>
                </div>
              </div>
            ) : null}

            {/* Results panel */}
            {results ? (
              <div style={{
                marginTop: 16, padding: '14px 16px',
                background: results.summary.failed === 0 ? C.accentSoft : C.warnSoft,
                border: `1px solid ${results.summary.failed === 0 ? C.accent : C.warn}`,
              }}>
                <div style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: results.summary.failed === 0 ? C.accentDeep : C.warn,
                  marginBottom: 8,
                }}>
                  Result · {results.summary.succeeded} ok · {results.summary.failed} failed
                </div>
                {results.results.filter(r => !r.success).length > 0 ? (
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: C.ink }}>
                    {results.results.filter(r => !r.success).slice(0, 6).map(r => (
                      <li key={r.student_id}>
                        <span style={{
                          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                        }}>#{r.student_id}</span>
                        {' — '}{r.error}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </section>
        </div>

        {/* FOOTER */}
        <div style={{
          padding: '14px 28px',
          borderTop: `1px solid ${C.rule}`,
          background: '#FFFFFF',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{
            fontSize: 11, color: C.muted,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            letterSpacing: '0.06em',
          }}>
            {selected.size > 0 && tgtLag
              ? <>READY · {selected.size} STUDENT(S) · → {tgtLag.name}</>
              : selected.size === 0
                ? 'select students above to enable promotion'
                : 'pick a target class to enable promotion'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 22px',
                background: '#FFFFFF', color: C.ink,
                border: `1px solid ${C.rule}`,
                borderRadius: '6px',
                fontSize: 13, fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="bp-action"
              style={{
                padding: '8px 22px',
                background: canSubmit ? C.accent : C.mutedSoft, color: '#FFFFFF',
                border: 'none', borderRadius: '6px',
                fontSize: 13, fontWeight: 600,
                cursor: canSubmit ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {submitting ? 'Promoting…' : `Promote ${selected.size || ''}`}
              <span aria-hidden style={{ fontSize: 14 }}>→</span>
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast ? (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.kind === 'danger' ? C.alarm : toast.kind === 'warn' ? C.warn : toast.kind === 'success' ? C.accent : C.muted,
          color: '#FFFFFF',
          padding: '10px 16px',
          borderRadius: '8px',
          fontSize: 13,
          maxWidth: 380,
          boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
          fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
          animation: 'bp-rise 200ms ease',
        }}>
          {toast.msg}
        </div>
      ) : null}
    </div>
  );
}
